import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    edgeKey,
    isRimEdge,
    isInside
} from '../../core/GridGeometry.js';
import type { Edge } from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';
import { FloodDebug, Rational, CellTag, Basin, rationalToNumber } from './FloodDebug.js';
import type { DropHint } from './FloodUtils.js';
import { stripSeparators } from '../stateSerialization.js';
import {
    reduceRational,
    addRationals,
    subtractRationals,
    multiplyRationals,
    divideRational,
    compareRationals
} from './FloodUtils.js';

export class FloodGame {
    protected grid: Grid;
    protected walls: Set<string> = new Set();  // Set of edgeKey strings
    protected faucetCol: number = 0;

    // Convenience accessors for grid subsystems
    protected get gs() { return this.grid.serializer; }

    // Computed state
    protected cellFillTime: Map<string, number> = new Map();
    protected digitPositions: Map<number, Position> = new Map();
    // Spill points (water tipping over an edge), keyed by "row,col|from" to dedupe
    // across the many flow recomputations within a single simulation.
    protected dropHints: Map<string, DropHint> = new Map();
    protected hasUnreachableCells: boolean = false;
    private reachableCells: Set<string> = new Set();
    private disconnectedWalls: Set<string> = new Set();  // Set of edgeKey strings

    // Debug helper (optional, for testing/dev)
    protected debug: FloodDebug = new FloodDebug();

    constructor(grid: Grid) {
        this.grid = grid;
        this.initializeDigitPositions();
        this.recalculate();
    }

    private initializeDigitPositions(): void {
        const positions = this.grid.getAllDigitPositions();
        for (const [digit, pos] of positions) {
            this.digitPositions.set(digit, pos);
        }
    }

    getGrid(): Grid {
        return this.grid;
    }

    // Enable debug logging at specific timestamps
    setDebugTimestamps(timestamps: number[]): void {
        this.debug.setTimestamps(timestamps);
    }

    // Log debug snapshots to console
    logDebugSnapshots(): void {
        this.debug.logSnapshots();
    }

    getFaucetCol(): number {
        return this.faucetCol;
    }

    getReachableCells(): Set<string> {
        return this.reachableCells;
    }

    getDisconnectedWalls(): Set<string> {
        return this.disconnectedWalls;
    }

    getHasUnreachableCells(): boolean {
        return this.hasUnreachableCells;
    }

    getWalls(): Set<string> {
        return this.walls;
    }

    getCellFillTime(row: number, col: number): number | null {
        return this.cellFillTime.get(positionKey({row, col})) ?? null;
    }

    // All spill points (over-an-edge drops) seen across the simulation.
    getDropHints(): DropHint[] {
        return [...this.dropHints.values()];
    }

    // Record a spill point: a cell where horizontal flow tips over an edge and
    // falls. `viaCellKey` is the neighbour the water stepped in from ("row,col"),
    // so the horizontal sign gives the side it arrived on; a vertical step (or a
    // gate) leaves no side, and the hint is drawn as a straight fall.
    //
    // This must be the *immediate* neighbour and not the crash the spread came
    // from. Water reaches a cell by whatever route the walls allow — around a
    // pocket, up from below — and the crash is regularly on the other side of a
    // wall from the step the water actually took, which drew the arrow arriving
    // through that wall.
    protected recordDropHint(row: number, col: number, viaCellKey: string | null): void {
        let from: 'W' | 'E' | null = null;
        if (viaCellKey) {
            const [viaRow, viaCol] = viaCellKey.split(',').map(Number);
            if (viaRow === row) {
                if (viaCol < col) from = 'W';
                else if (viaCol > col) from = 'E';
            }
        }
        const key = `${row},${col}|${from}`;
        if (!this.dropHints.has(key)) this.dropHints.set(key, { row, col, from });
    }

    // Check if there's a wall on the given edge
    hasWall(edge: Edge): boolean {
        return this.walls.has(edgeKey(edge));
    }

    // Check if there's a user-placed wall in the given direction from pos
    hasWallBetween(pos: Position, dir: Direction): boolean {
        const edge = this.grid.getEdgeFromPosition(pos, dir);
        return this.walls.has(edgeKey(edge));
    }

    // Check if there's a wall in the given direction from pos (rim or user-placed)
    protected hasWallToward(pos: Position, dir: Direction): boolean {
        const edge = this.grid.getEdgeFromPosition(pos, dir);
        return isRimEdge(edge) || this.walls.has(edgeKey(edge));
    }

    setFaucet(col: number): void {
        if (col >= 0 && col < this.grid.size) {
            this.faucetCol = col;
            this.recalculate();
        }
    }

    // Toggle wall on the given edge
    toggleWall(edge: Edge): boolean {
        // Don't allow walls on the rim
        if (isRimEdge(edge)) return false;

        const key = edgeKey(edge);
        if (this.walls.has(key)) {
            this.walls.delete(key);
        } else {
            this.walls.add(key);
        }
        this.recalculate();
        return true;
    }

    /**
     * Flood-fill the cells the water can actually get to, and flag the board when some
     * can't be reached — keeping {@link getReachableCells} in step so the renderer can
     * shade what *is* reachable. Purely structural: it reads the walls, not the flow,
     * so it holds even on a board whose walls are disconnected.
     */
    protected updateReachability(): void {
        const reachable = this.reachableFrom(this.faucetCol);
        this.hasUnreachableCells = reachable.size < this.grid.size * this.grid.size;
        this.reachableCells = this.hasUnreachableCells ? reachable : new Set();
    }

    /** The flood-fill reachability rests on; {@link FloodVortex} follows gates too. */
    protected reachableFrom(faucetCol: number): Set<string> {
        return this.floodFillReachable(faucetCol);
    }

    protected validateWallConnectivity(): boolean {
        this.disconnectedWalls.clear();

        if (this.walls.size === 0) return true;

        const connected = new Set<string>();
        const queue: string[] = [];

        // Find walls that touch the boundary
        for (const wallKey of this.walls) {
            const edge = this.grid.deserializeEdge(wallKey);
            if (edge && this.grid.edgeTouchesRim(edge)) {
                connected.add(wallKey);
                queue.push(wallKey);
            }
        }

        if (queue.length === 0) {
            // All walls are disconnected
            for (const w of this.walls) this.disconnectedWalls.add(w);
            return false;
        }

        // BFS to find connected walls
        while (queue.length > 0) {
            const currentKey = queue.shift()!;
            const currentEdge = this.grid.deserializeEdge(currentKey);
            if (!currentEdge) continue;

            const adjacent = this.grid.getEdgesTouchingEdge(currentEdge);
            for (const adjEdge of adjacent) {
                // Skip rim edges
                if (isRimEdge(adjEdge)) continue;

                const adjKey = edgeKey(adjEdge);
                if (this.walls.has(adjKey) && !connected.has(adjKey)) {
                    connected.add(adjKey);
                    queue.push(adjKey);
                }
            }
        }

        // Store disconnected walls
        for (const w of this.walls) {
            if (!connected.has(w)) {
                this.disconnectedWalls.add(w);
            }
        }

        return connected.size === this.walls.size;
    }


    // Check if cell below is accessible (no wall blocking)
    protected isBelowAccessible(row: number, col: number): boolean {
        return !this.hasWallToward({ row, col }, 'S');
    }

    // Flood-fill to find all reachable cells from faucet (ignoring flow dynamics)
    protected floodFillReachable(faucetCol: number): Set<string> {
        const reachable = new Set<string>();
        const queue: Position[] = [{ row: 0, col: faucetCol }];
        const directions: Direction[] = ['N', 'S', 'E', 'W'];

        while (queue.length > 0) {
            const pos = queue.shift()!;
            const key = positionKey(pos);
            if (reachable.has(key)) continue;
            reachable.add(key);

            for (const dir of directions) {
                if (!this.hasWallToward(pos, dir)) {
                    const neighbor = this.grid.move(pos, dir);
                    if (isInside(neighbor)) {
                        queue.push(neighbor);
                    }
                }
            }
        }

        return reachable;
    }

    // Get horizontal neighbors (left/right) without walls
    protected getHorizontalNeighbors(row: number, col: number): string[] {
        const pos: Position = { row, col };
        const neighbors: string[] = [];
        for (const dir of ['W', 'E'] as Direction[]) {
            if (!this.hasWallToward(pos, dir)) {
                const neighbor = this.grid.move(pos, dir);
                if (isInside(neighbor)) {
                    neighbors.push(positionKey(neighbor));
                }
            }
        }
        return neighbors;
    }

    // Get all connected neighbors (up/down/left/right) without walls
    protected getConnectedNeighbors(row: number, col: number): string[] {
        const pos: Position = { row, col };
        const neighbors: string[] = [];
        for (const dir of ['N', 'S', 'E', 'W'] as Direction[]) {
            if (!this.hasWallToward(pos, dir)) {
                const neighbor = this.grid.move(pos, dir);
                if (isInside(neighbor)) {
                    neighbors.push(positionKey(neighbor));
                }
            }
        }
        return neighbors;
    }

    // Tag a cell if not already tagged
    private tagCell(
        ck: string,
        tag: CellTag,
        flowState: Map<string, CellTag>,
        toResolve: string[]
    ): void {
        if (!flowState.has(ck)) {
            flowState.set(ck, tag);
            toResolve.push(ck);
        }
    }

    // Resolve a drop cell
    private resolveDrop(
        ck: string,
        flowState: Map<string, CellTag>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: string[]
    ): void {
        const tag = flowState.get(ck);
        if (!tag || tag.type !== 'drop' || tag.resolved) return;

        const [row, col] = ck.split(',').map(Number);

        // If filled, mark resolved and return
        if (filled.has(ck)) {
            tag.resolved = true;
            return;
        }

        // If partially filled, retag as crash
        if (waterLevel.has(ck)) {
            const crashTag: CellTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            flowState.set(ck, crashTag);
            this.resolveCrash(ck, flowState, filled, waterLevel, toResolve);
            return;
        }

        // Check cell below
        const belowKey = positionKey({row: row + 1, col});

        // If wall below or at bottom
        if (!this.isBelowAccessible(row, col)) {
            const crashTag: CellTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            flowState.set(ck, crashTag);
            this.resolveCrash(ck, flowState, filled, waterLevel, toResolve);
            return;
        }

        // If cell below is filled
        if (filled.has(belowKey)) {
            const crashTag: CellTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            flowState.set(ck, crashTag);
            this.resolveCrash(ck, flowState, filled, waterLevel, toResolve);
            return;
        }

        // Otherwise, tag cell below as drop and continue
        tag.resolved = true;
        const dropTag: CellTag = { type: 'drop', parent: ck, rate: tag.rate, resolved: false };
        this.tagCell(belowKey, dropTag, flowState, toResolve);
    }

    // Resolve a crash cell
    private resolveCrash(
        ck: string,
        flowState: Map<string, CellTag>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: string[]
    ): void {
        const tag = flowState.get(ck);
        if (!tag || tag.type !== 'crash' || tag.resolved) return;

        const [row, col] = ck.split(',').map(Number);
        tag.resolved = true;

        // If partially filled, tag all connected non-empty cells as spread
        if (waterLevel.has(ck)) {
            const neighbors = this.getConnectedNeighbors(row, col);
            for (const nk of neighbors) {
                // Tag non-empty cells (partially filled OR fully filled)
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagCell(nk, { type: 'spread', parent: ck, via: ck, resolved: false }, flowState, toResolve);
                }
            }
            return;
        }

        const belowKey = positionKey({row: row + 1, col});

        // If wall below, tag horizontal neighbors as spread
        if (!this.isBelowAccessible(row, col)) {
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCell(nk, { type: 'spread', parent: ck, via: ck, resolved: false }, flowState, toResolve);
                }
            }
            return;
        }

        // Otherwise cell below is filled - tag both B and horizontal neighbors
        const neighbors = this.getHorizontalNeighbors(row, col);
        for (const nk of neighbors) {
            if (!filled.has(nk)) {
                this.tagCell(nk, { type: 'spread', parent: ck, via: ck, resolved: false }, flowState, toResolve);
            }
        }
        // Tag the filled cell below as spread (to propagate through filled region)
        this.tagCell(belowKey, { type: 'spread', parent: ck, via: ck, resolved: false }, flowState, toResolve);
    }

    // Resolve a spread cell
    private resolveSpread(
        ck: string,
        flowState: Map<string, CellTag>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: string[],
        waitingDrops: string[]
    ): void {
        const tag = flowState.get(ck);
        if (!tag || tag.type !== 'spread' || tag.resolved) return;

        const [row, col] = ck.split(',').map(Number);
        const parentCrash = tag.parent;
        tag.resolved = true;

        // If filled, tag all connected cells as spread
        if (filled.has(ck)) {
            const neighbors = this.getConnectedNeighbors(row, col);
            for (const nk of neighbors) {
                this.tagCell(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, flowState, toResolve);
            }
            return;
        }

        // If partially filled, tag connected non-empty cells as spread
        if (waterLevel.has(ck)) {
            const neighbors = this.getConnectedNeighbors(row, col);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagCell(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, flowState, toResolve);
                }
            }
            return;
        }

        // Cell is empty
        const belowKey = positionKey({row: row + 1, col});

        // If wall below, tag horizontal neighbors only
        if (!this.isBelowAccessible(row, col)) {
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCell(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, flowState, toResolve);
                }
            }
            return;
        }

        // If cell below is filled, tag both B and horizontal neighbors (like crash)
        if (filled.has(belowKey)) {
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCell(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, flowState, toResolve);
                }
            }
            // Tag the filled cell below to propagate through filled region
            this.tagCell(belowKey, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, flowState, toResolve);
            return;
        }

        // Cell below is empty - this becomes a drop (escape). The side is the step
        // the water took to get here, not the crash it ultimately came from.
        this.recordDropHint(row, col, tag.via ?? null);
        const dropTag: CellTag = { type: 'drop', parent: parentCrash, rate: null, resolved: false };
        flowState.set(ck, dropTag);
        waitingDrops.push(ck);
    }

    // Build flow state for current fill configuration
    private buildFlowState(
        faucetCol: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>
    ): { flowState: Map<string, CellTag>; basins: Basin[] } {
        const flowState = new Map<string, CellTag>();
        const toResolve: string[] = [];
        const waitingDrops: string[] = [];

        // Start with cell below faucet as drop with rate 1
        const startCell = positionKey({row: 0, col: faucetCol});
        const startTag: CellTag = { type: 'drop', parent: null, rate: { num: 1, denom: 1 }, resolved: false };
        flowState.set(startCell, startTag);
        toResolve.push(startCell);

        // Resolve all cells
        while (toResolve.length > 0) {
            const ck = toResolve.shift()!;
            const tag = flowState.get(ck);
            if (!tag || tag.resolved) continue;

            if (tag.type === 'drop') {
                this.resolveDrop(ck, flowState, filled, waterLevel, toResolve);
            } else if (tag.type === 'crash') {
                this.resolveCrash(ck, flowState, filled, waterLevel, toResolve);
            } else if (tag.type === 'spread') {
                this.resolveSpread(ck, flowState, filled, waterLevel, toResolve, waitingDrops);
            }
        }

        // Process waiting drops iteratively until none remain
        while (waitingDrops.length > 0) {
            // Group by parent crash
            const byParent = new Map<string, string[]>();
            const currentWaiting = [...waitingDrops];
            waitingDrops.length = 0; // Clear for next iteration

            for (const dk of currentWaiting) {
                const tag = flowState.get(dk) as CellTag & { type: 'drop' };
                const parent = tag.parent!;
                if (!byParent.has(parent)) byParent.set(parent, []);
                byParent.get(parent)!.push(dk);
            }

            // Assign rates and resolve
            for (const [parentKey, children] of byParent) {
                const parentTag = flowState.get(parentKey);
                if (!parentTag || parentTag.type !== 'crash') continue;

                const parentRate = parentTag.rate;
                const childRate = divideRational(parentRate, children.length);

                for (const dk of children) {
                    const tag = flowState.get(dk) as CellTag & { type: 'drop' };
                    tag.rate = childRate;
                    toResolve.push(dk);
                }
            }

            // Resolve the waiting drops (may add more to waitingDrops)
            while (toResolve.length > 0) {
                const ck = toResolve.shift()!;
                const tag = flowState.get(ck);
                if (!tag || tag.resolved) continue;

                if (tag.type === 'drop') {
                    this.resolveDrop(ck, flowState, filled, waterLevel, toResolve);
                } else if (tag.type === 'crash') {
                    this.resolveCrash(ck, flowState, filled, waterLevel, toResolve);
                } else if (tag.type === 'spread') {
                    this.resolveSpread(ck, flowState, filled, waterLevel, toResolve, waitingDrops);
                }
            }
        }

        // Find terminal crashes (crashes that are not parents of any drop)
        const dropParents = new Set<string>();
        for (const [, tag] of flowState) {
            if (tag.type === 'drop' && tag.parent) {
                dropParents.add(tag.parent);
            }
        }

        const basins: Basin[] = [];
        for (const [ck, tag] of flowState) {
            if (tag.type === 'crash' && !dropParents.has(ck)) {
                // Terminal crash - collect basin cells
                const basinCells: string[] = [ck];
                // Add all non-filled spread children
                for (const [sk, stag] of flowState) {
                    if (stag.type === 'spread' && stag.parent === ck && !filled.has(sk)) {
                        basinCells.push(sk);
                    }
                }
                basins.push({ cells: basinCells, rate: tag.rate });
            }
        }

        return { flowState, basins };
    }

    protected recalculate(): void {
        this.simulate();
    }

    // Export walls using cell-based edge serialization: "A6A7, B8C8, ..."
    protected exportWalls(): string {
        const wallStrings: string[] = [];

        for (const wallKey of this.walls) {
            const edge = this.grid.deserializeEdge(wallKey);
            if (edge) wallStrings.push(this.gs.serializeEdgeCells(edge));
        }

        return wallStrings.join(', ');
    }

    protected simulate(): void {
        this.cellFillTime.clear();
        this.reachableCells.clear();
        this.dropHints.clear();
        this.debug.clear();

        // Work out reachability first. It's a plain structural flood-fill that owes
        // nothing to the flow simulation, and a board can be walled off *and*
        // disconnected at once — so deciding it up front is what lets the walled-off
        // region still be reported and shaded when the walls are also disconnected.
        this.updateReachability();

        if (!this.validateWallConnectivity()) return;
        if (this.hasUnreachableCells) return; // flagged and shaded; timings are moot

        const filled = new Set<string>();
        const waterLevel = new Map<string, Rational>(); // Per-cell water level (0 to 1)
        let currentTime: Rational = { num: 0, denom: 1 };
        let debugTimestampIndex = 0;

        const maxIterations = this.grid.size * this.grid.size * 2;
        let iterations = 0;

        while (iterations++ < maxIterations && rationalToNumber(currentTime) < 100) {
            // Build flow state using new tag-based algorithm
            const { flowState, basins } = this.buildFlowState(this.faucetCol, filled, waterLevel);

            // Capture snapshots at debug timestamps (now with flow state)
            debugTimestampIndex = this.debug.maybeCaptureSnapshot(
                rationalToNumber(currentTime),
                debugTimestampIndex,
                filled,
                waterLevel,
                flowState,
                basins
            );

            if (basins.length === 0) {
                break;
            }

            // Calculate fill time for each basin
            const basinFillTimes: Array<{ basin: Basin; fillTime: Rational; perCellRate: Rational }> = [];

            for (const basin of basins) {
                const numCells = basin.cells.length;
                const perCellRate = divideRational(basin.rate, numCells);
                const sampleCell = basin.cells[0];
                const currentLevel = waterLevel.get(sampleCell) ?? { num: 0, denom: 1 };
                const remaining = subtractRationals({ num: 1, denom: 1 }, currentLevel);

                if (compareRationals(remaining, { num: 0, denom: 1 }) <= 0) {
                    // Already full - shouldn't happen, but skip
                    continue;
                }

                // timeToFill = remaining / perCellRate
                const timeToFill: Rational = reduceRational({
                    num: remaining.num * perCellRate.denom,
                    denom: remaining.denom * perCellRate.num
                });
                const fillTime = addRationals(currentTime, timeToFill);

                basinFillTimes.push({ basin, fillTime, perCellRate });
            }

            if (basinFillTimes.length === 0) {
                break;
            }

            // Find earliest fill time
            let earliestTime = basinFillTimes[0].fillTime;
            for (const { fillTime } of basinFillTimes) {
                if (compareRationals(fillTime, earliestTime) < 0) {
                    earliestTime = fillTime;
                }
            }

            // Advance time and update water levels for ALL basins
            const elapsed = subtractRationals(earliestTime, currentTime);
            for (const { basin, perCellRate } of basinFillTimes) {
                const added = multiplyRationals(elapsed, perCellRate);
                for (const ck of basin.cells) {
                    const current = waterLevel.get(ck) ?? { num: 0, denom: 1 };
                    waterLevel.set(ck, addRationals(current, added));
                }
            }

            // Mark ALL basins that complete at the earliest time
            const fillTimeNum = Math.round(rationalToNumber(earliestTime) * 10) / 10;
            for (const { basin, fillTime } of basinFillTimes) {
                if (compareRationals(fillTime, earliestTime) === 0) {
                    for (const ck of basin.cells) {
                        this.cellFillTime.set(ck, fillTimeNum);
                        filled.add(ck);
                    }
                }
            }

            currentTime = earliestTime;
        }

        // Check if flow algorithm filled all cells
        const filledCount = this.cellFillTime.size;
        if (filledCount < this.grid.size * this.grid.size) {
            return;
        }
    }

    checkWin(): boolean {
        let previousTime = -1;
        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) return false;

            const time = this.cellFillTime.get(positionKey(pos));
            if (time === undefined) return false;
            if (time <= previousTime) return false;

            previousTime = time;
        }
        return true;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const wallCount = this.walls.size;

        // Time spread: difference between fill time of last and first digit
        const posMin = this.digitPositions.get(this.grid.minDigit);
        const posMax = this.digitPositions.get(this.grid.maxDigit);
        if (!posMin || !posMax) return null;

        const timeMin = this.cellFillTime.get(positionKey(posMin));
        const timeMax = this.cellFillTime.get(positionKey(posMax));
        if (timeMin === undefined || timeMax === undefined) return null;

        const timeSpread = Math.round(timeMax - timeMin);

        return calculateScore(wallCount, timeSpread);
    }

    /** Scoring-rules overlay: time spread (secondary) as an arc between the min/max
     *  digit. Wall segments (primary) are redrawn in amber by the renderer. */
    getScoringHighlight(): ScoringHighlight {
        const from = this.grid.getDigitPosition(this.grid.minDigit);
        const to = this.grid.getDigitPosition(this.grid.maxDigit);
        if (!from || !to) return {};
        const fromValue = Math.round(this.cellFillTime.get(positionKey(from)) ?? 0);
        const toValue = Math.round(this.cellFillTime.get(positionKey(to)) ?? 0);
        return { spreadArc: { from, to, fromValue, toValue, primary: false } };
    }

    getCorrectDigits(): number[] {
        const correct: number[] = [];
        let previousTime = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) break;

            const time = this.cellFillTime.get(positionKey(pos));
            if (time === undefined) break;
            if (time <= previousTime) break;

            correct.push(digit);
            previousTime = time;
        }
        return correct;
    }

    /**
     * The first pair of digits that fill out of order: `before` should fill
     * before `after`, but currently fills at the same time or later. Returns null
     * if every filled digit is in order (a later digit simply not filled yet is
     * not a violation).
     */
    getFirstFillOrderViolation(): { before: number; after: number } | null {
        let previousTime = -1;
        let previousDigit = -1;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = this.digitPositions.get(digit);
            if (!pos) break;

            const time = this.cellFillTime.get(positionKey(pos));
            if (time === undefined) break;  // not filled yet — not an order violation
            if (time <= previousTime) return { before: previousDigit, after: digit };

            previousTime = time;
            previousDigit = digit;
        }
        return null;
    }

    /** Remove fill times beyond maxTime (for tutorial progressive display). */
    limitDisplayTime(maxTime: number): void {
        for (const [key, time] of this.cellFillTime) {
            if (time > maxTime) {
                this.cellFillTime.delete(key);
            }
        }
    }

    isStarted(): boolean {
        return this.faucetCol !== 0 || this.walls.size > 0;
    }

    reset(): void {
        this.faucetCol = 0;
        this.walls.clear();
        this.cellFillTime.clear();
        this.reachableCells.clear();
        this.dropHints.clear();
        this.disconnectedWalls.clear();
        this.hasUnreachableCells = false;
        this.recalculate();
    }

    /**
     * Jumpstart a solution: cup the first digit with walls (open at the top) and
     * put the faucet straight above it, so it fills first — guaranteeing it fills
     * before digit 2. When the digit is interior, a vertical "pole" extends one
     * cup wall down to the bottom rim so the wall structure stays anchored.
     * Verified against the flow simulator.
     */
    autoStart(): boolean {
        if (this.isStarted()) return false;

        const min = this.grid.minDigit;
        const d1 = this.digitPositions.get(min);
        const d2 = this.digitPositions.get(min + 1);
        if (!d1 || !d2) return false;

        const size = this.grid.size;
        const totalCells = size * size;

        for (const pole of ['none', 'left', 'right'] as const) {
            this.reset();
            this.faucetCol = d1.col;

            // Cup: wall the digit's sides except the top (water enters from above).
            this.addCupWall(d1, 'S');
            this.addCupWall(d1, 'W');
            this.addCupWall(d1, 'E');

            // Pole: extend a side wall down to the bottom rim to anchor the cup.
            if (pole === 'left' && d1.col > 0) {
                for (let r = d1.row + 1; r < size; r++) this.addCupWall({ row: r, col: d1.col }, 'W');
            } else if (pole === 'right' && d1.col < size - 1) {
                for (let r = d1.row + 1; r < size; r++) this.addCupWall({ row: r, col: d1.col }, 'E');
            }

            this.recalculate();

            if (this.cellFillTime.size === totalCells
                && !this.hasUnreachableCells
                && this.getCorrectDigits().length >= 2) {
                return true;
            }
        }

        this.reset();
        return false;
    }

    /** Add a wall on the given side of a cell, unless that side is the rim. */
    private addCupWall(pos: Position, dir: Direction): void {
        const edge = this.grid.getEdgeFromPosition(pos, dir);
        if (!isRimEdge(edge)) this.walls.add(edgeKey(edge));
    }

    getStatus(): string {
        const boldRed = (t: string) => `<span style="color:#e94560;font-weight:bold">${t}</span>`;

        if (this.checkWin()) {
            return 'You win! All digits filled in order!';
        }

        // The only real error states: regions the water can never get to, and walls
        // that don't reach the rim. Out-of-order fills are not an error — on most
        // boards the digits start out of order, so the default hint below just steers
        // you.
        //
        // Walling a region off comes first: it's the more concrete break, and a wall
        // that seals a region is usually disconnected as well, so naming only the
        // connectivity problem would point at the symptom rather than the cause.
        if (this.hasUnreachableCells) {
            return `${boldRed('Invalid')}: some cells are walled off and cannot be reached.`;
        }

        if (!this.validateWallConnectivity()) {
            return `${boldRed('Invalid')}: walls must connect to the grid edge.`;
        }

        const [before, after] = this.getFillPairToEstablish();
        return `Click edges to place walls and fill ${before} before ${after}. ` +
            `Click top edges to move the faucet.`;
    }

    /**
     * The two digits the player is currently trying to order: the boundary of the
     * correct run and the next digit after it — the pair "fill X before Y" names.
     * Falls back to the first two digits before any are correctly in order.
     */
    getFillPairToEstablish(): [number, number] {
        const frontier = Math.max(1, this.getCorrectDigits().length);
        return [this.grid.minDigit + frontier - 1, this.grid.minDigit + frontier];
    }

    /**
     * Serialize game state to string format.
     * Format: "D,A0A1,B2B3" where D is faucet column letter and pairs are wall edges
     */
    serialize(): string {
        const faucetLetter = String.fromCharCode('A'.charCodeAt(0) + this.faucetCol);
        const wallParts: string[] = [];

        for (const wallKey of this.walls) {
            const edge = this.grid.deserializeEdge(wallKey);
            if (edge) {
                wallParts.push(this.gs.serializeEdgeCells(edge));
            }
        }

        if (wallParts.length === 0) {
            return faucetLetter;
        }
        return faucetLetter + wallParts.join('');
    }

    /**
     * Deserialize game state from string format.
     */
    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        const compact = stripSeparators(serialized);
        if (!compact) return;

        // First char is the faucet column.
        const faucetLetter = compact[0];
        const maxLetter = String.fromCharCode('A'.charCodeAt(0) + this.grid.size - 1);
        if (faucetLetter >= 'A' && faucetLetter <= maxLetter) {
            this.faucetCol = faucetLetter.charCodeAt(0) - 'A'.charCodeAt(0);
        }

        // Remaining 4-char chunks are wall edges.
        for (let i = 1; i + 4 <= compact.length; i += 4) {
            try {
                const edge = this.grid.deserializeEdgeCells(compact.slice(i, i + 4));
                if (edge && !isRimEdge(edge)) {
                    this.walls.add(edgeKey(edge));
                }
            } catch {
                // Invalid edge format, skip
            }
        }

        this.recalculate();
    }
}
